//// @ts-nocheck

import { useEffect, useRef, useState } from 'react'
import * as d3 from 'd3'

import styles from './ConnectionsGraph.module.scss'
import { useResizeObserver } from '../../hooks/useResizeObserver'
import { User } from '../../types/User'
import { createBubbleGenerator } from './visualization/bubble'
import { randomNumberFromInterval } from '../../utils/math-utils'
import { ReactComponent as CenterX } from './visualization/center-x.svg'

type UserNode = User & {
  selected?: boolean
  radius: number
  x: number
  y: number
  repelStrength?: number
  selectionLevel?: 0 | 1 | 2
  isCenterNode?: boolean
  moodColor: string
  inTransition?: boolean
} & d3.SimulationNodeDatum




export const MOODS = [
  // { colorCode: '#FAF285', mood: 'Mature' }, // YELLOW, should be gray? 
  // { colorCode: '#B7B7B7', mood: 'Mature' }, // GRAY, should be gray? 
  { colorCode: '#FBA919', mood: 'Optimistic' }, // ORANGE
  { colorCode: '#EE2741', mood: 'Lively' }, // RED, should be peach?
  { colorCode: '#EB659D', mood: 'Flirtatious' }, // PINK
  { colorCode: '#8859A5', mood: 'Creative' }, // PURPLE
  { colorCode: '#4F9ED7', mood: 'Calm' }, // BLUE
  // { colorCode: '#92D4D5', mood: 'Healing' }, // TURQUOISE
  { colorCode: '#80C892', mood: 'Healthy' }, // GREEN
] as const

export type MoodId = typeof MOODS[number]['mood']

const MOOD_MAP = MOODS.reduce<Partial<Record<MoodId, string>>>((prev, curr) => {
  prev[curr.mood] = curr.colorCode
  return prev
}, {})

type Props = {
  data?: User[] | null
  filterBy: MoodId | null
  onButtonClick: () => void
}

const FIRST_SELECTION_SIZE = 80
const SECOND_SELECTION_SIZE = 100

/* 
  YELLOW #FAF285 Mature?
  ORANGE #FBA919 Optimistic
  RED #EE2741 Lively ?
  PINK #EB659D Flirtatious
  PURPLE #8859A5 Creative
  BLUE #4F9ED7 Calm
  TURQUOISE #92D4D5 Healing
  GREEN #80C892 Healthy
*/

function getRandomMoodColor() {
  const moodColors = MOODS.map(x => x.colorCode)

  const index = randomNumberFromInterval(0, moodColors.length - 1)
  return moodColors[index]
}

export function ConnectionsGraph(props: Props): JSX.Element {
  const [userNodes, setUserNodes] = useState<UserNode[] | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const dimensions = useResizeObserver(containerRef)
  const svgRef = useRef<SVGSVGElement>(null)
  const visContainerRef = useRef<SVGGElement>(null)

  useEffect(() => {
    if (!visContainerRef.current) return
    const g = d3.select(visContainerRef.current)
    return () => {
      g.selectAll('*').remove()
    }
  }, [])

  useEffect(() => {
    if (!props.data) {
      return
    }

    if (!containerRef.current) {
      return
    }
    const container = containerRef.current
    const width = container.offsetWidth
    const height = container.offsetHeight

    const getNextBubble = createBubbleGenerator({ containerWidth: width, containerHeight: height })

    const filteredMoodColor = props.filterBy ? MOOD_MAP[props.filterBy] : null

    const result = props.data.map((user) => {
      const bubbleProps = getNextBubble()

      return {
        ...bubbleProps,
        ...user,
        moodColor: filteredMoodColor || getRandomMoodColor(),
      }
    })
    const centerNode = {
      ...result[0],
      id: -5,
      isCenterNode: true,
      radius: 0, // "Invisible" node. Displaying X instead.
      fx: width / 2,
      fy: height / 2,
    }
    setUserNodes([centerNode, ...result])
  }, [props.data])

  useEffect(() => {
    if (!userNodes || !visContainerRef.current) return
    const visContainer = d3.select<SVGGElement, unknown>(visContainerRef.current)
    // Remove all the bubbles when the user list state changes. New ones will be generated based on the new list.
    return () => {
      visContainer.selectAll('*').remove()
    }
  }, [userNodes])

  useEffect(() => {
    const container = containerRef.current
    if (!container || !userNodes || !svgRef.current || !visContainerRef.current || !dimensions) {
      return
    }
    const { width, height } = dimensions
    const center = { x: width / 2, y: height / 2 }
    const svg = d3.select<SVGSVGElement, unknown>(svgRef.current)
    const visContainer = d3.select<SVGGElement, unknown>(visContainerRef.current)

    svg.attr('width', width).attr('height', height).style('background-color', '#010101')

    const centerNode = userNodes.find((x) => x.isCenterNode)

    // Keep the center node in the center even when resizing the view.
    if (centerNode) {
      centerNode.fx = center.x
      centerNode.fy = center.y
    }

    const simulation = d3
      .forceSimulation(userNodes)
      .alpha(0.6)
      .alphaDecay(0.01)
      .alphaTarget(0.1) // As long as this is larger than alpha min, the simulation never stops
      .alphaMin(0.001)
      .force(
        'charge',
        d3.forceManyBody<UserNode>().strength((userNode) => {
          if (userNode.isCenterNode) return -100

          if (userNode.repelStrength) {
            return userNode.repelStrength
          }
          return 80
        }) // Positive attracts, negative repels
      )
      .force(
        'collision',
        d3
          .forceCollide<UserNode>()
          .radius((d) => {
            if (d.isCenterNode) return 80
            return d.radius + 4
          })
          .strength(1)
          .iterations(10)
      )
      .force('x', d3.forceX<UserNode>().strength(0.0002).x(center.x))

    simulation.stop()

    const g = visContainer
      .selectAll<SVGGElement, UserNode>('g')
      .data(userNodes, (user) => String(user.id))
      .join(
        (enter) => {
          const userNode = enter.append('g').attr('id', (user) => `node-${user.id}`)

          userNode
            .append('defs')
            .append('pattern')
            .attr('id', (user) => `image-${user.id}`)
            .attr('x', '0')
            .attr('y', '0')
            .attr('height', '100%')
            .attr('width', '100%')
            .attr('viewBox', `0 0 1 1`) // for preserving aspect ratio, must match height and width below
            .append('image')
            .attr('preserveAspectRatio', 'xMidYMid slice')
            .attr('x', '0%')
            .attr('y', '0%')
            .attr('height', 1)
            .attr('width', 1)
            .attr('xlink:href', (user) => `/img/user_photos/200x200${user.image}.jpg`)
            .attr('class', styles.someClass)

          userNode
            .append('circle')
            .attr('id', (user) => `circle-${user.id}`)
            .attr('class', 'user-circle')
            .attr('cx', (user) => user.x)
            .attr('cy', (user) => user.y)
            .transition()
            .duration((user) => Math.log(user.radius) * randomNumberFromInterval(100, 200))
            .attr('r', (user) => user.radius)

          userNode
            .attr('fill', (user) => `url(#image-${user.id})`)
            .attr('stroke', (user) => user.moodColor)
            .attr('stroke-width', 2)

          userNode.on('click', function (e, user) {
            console.log('Clicked', user)
            if (user.isCenterNode) return
            const circle = d3.select<d3.BaseType, UserNode>(`#circle-${user.id}`)
            let newRadius: number
            if (user.inTransition) return // Disable clicking while still transitioning from the previous one.
            if (!user.selectionLevel) {
              newRadius = FIRST_SELECTION_SIZE
              user.selectionLevel = 1
              circle.attr('stroke-width', '3')
            } else if (user.selectionLevel === 1) {
              user.selectionLevel = 2
              // repellionStrength = -2000
              newRadius = SECOND_SELECTION_SIZE
              circle.attr('stroke-width', '4')
            } else {
              newRadius = 30
              // repellionStrength = -2000
              user.selectionLevel = 0
              circle.attr('stroke-width', '2')
            }

            const repellionStrength = -1 * newRadius * 30

            // Fix the position
            user.fx = user.x
            user.fy = user.y
            user.inTransition = true

            simulation.nodes(userNodes)
            setTimeout(() => {
              // Release the position
              user.fx = null
              user.fy = null
              user.inTransition = false
            }, 500)

            circle
              .transition()
              .duration(500)
              .tween('radius', (user) => {
                const interpolate = d3.interpolate(user.radius, newRadius)
                const repellion = d3.interpolate(repellionStrength, 0)
                return (t) => {
                  const radius = interpolate(t)
                  circle.attr('r', radius)
                  user.radius = radius
                  user.repelStrength = repellion(t)
                  simulation.nodes(userNodes)
                }
              })
              .attr('r', (user) => user.radius)

            simulation.nodes(userNodes)
            simulation.alpha(0.5).restart()
          })

          return userNode
        },
        (update) => update,
        (exit) => {
          const transitionDuration = 250
          exit.selectAll('.user-circle').transition().duration(transitionDuration).attr('r', 0)

          // Adding an empty transition so the remove() is delayed until the actual transition finishes.
          return exit.transition().duration(transitionDuration).remove()
        }
      )
      .attr('class', 'user-node')

    const circles = g.selectAll<d3.BaseType, UserNode>('.user-circle')

    const wallDistance = width < 700 ? 1100 : 850

    simulation
      .nodes(userNodes)
      .on('tick', () => {
        circles
          .attr(
            'cx',
            (d) =>
              (d.x = Math.max(
                d.radius + 3 - wallDistance,
                Math.min(width + wallDistance - d.radius - 3, d.x)
              ))
          )
          // https://bl.ocks.org/puzzler10/2531c035e8d514f125c4d15433f79d74
          // Bounding box for top and bottom
          .attr('cy', (d) => (d.y = Math.max(d.radius + 3, Math.min(height - d.radius - 3, d.y))))
      })
      .restart()

    svg.call(
      d3
        .drag<any, any>()
        .on('start', (e, d) => {
          //
        })
        .on('drag', (e, d) => {
          userNodes.forEach((node) => {
            const { vx, vy } = getVelocity(
              { x: node.x, y: node.y },
              { x: e.x, y: e.y },
              { dx: e.dx, dy: e.dy }
            )

            node.vx = vx
            node.vy = vy
          })
        })
        .on('end', (e, d) => {
          //
        })
    )

    return () => {
      g.selectAll('.center-circle').remove()
      simulation.stop()
    }
  }, [userNodes, dimensions])

  return (
    <div ref={containerRef} className={styles.connectionsGraph}>
      <svg ref={svgRef}>
        <g className={styles.visualizationContainer} ref={visContainerRef}></g>
      </svg>
      <div id={styles.centerX}>
        <CenterX onClick={props.onButtonClick} />
      </div>
    </div>
  )
}

// TODO
// Closest to the cursor get the fastest velocity and then the others get smaller velocity based on distance
// Uhh... Just calculate the velocity as a formula from the distance instead of doing some stupid shit with area.......
function getVelocity(
  nodePos: { x: number; y: number },
  dragPos: { x: number; y: number },
  dragDelta: { dx: number; dy: number }
): { vx: number; vy: number } {
  const area = 50
  const diffX = Math.abs(nodePos.x - dragPos.x)
  const diffY = Math.abs(nodePos.y - dragPos.y)
  const xScale = d3.scaleLinear().domain([0, 1440]).range([1.9, 1.25])
  const yScale = d3.scaleLinear().domain([0, 600]).range([1.2, 0.1])

  // const multiplier = 15 // 5 - 20
  const vx = dragDelta.dx * xScale(diffX)
  const vy = dragDelta.dy * xScale(diffY)
  // const distance = Math.sqrt(Math.pow(diffX, 2) + Math.pow(diffY, 2))
  // console.log('>>> distance', distance)
  // dinstace =
  // c2 = a2 + b2
  // if (node.)
  return { vx, vy }
}

/* 
  
*/
